import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  ThemeProvider, createTheme, CssBaseline, AppBar, Toolbar, Typography,
  Container, Box, Button, IconButton, Paper, Grid, Card, CardContent,
  Menu, MenuItem,
} from '@mui/material';
import {
  Brightness4, Brightness7, TrendingUp, ShowChart, AccountBalance,
  Dashboard as DashboardIcon, Settings as StrategyIcon, TrendingUpOutlined,
  ListAlt, SettingsInputComponent, Storage, AccountBalanceWallet, Webhook,
  Settings as SettingsIcon, Assessment, KeyboardArrowDown,
} from '@mui/icons-material';
import axios from 'axios';
import './App.css';
import AuthDialog from './components/AuthDialog';
import Dashboard from './components/Dashboard';
import StrategyManager from './components/Strategies/StrategyManager';
import ArbitrageOpportunities from './components/Arbitrage/ArbitrageOpportunities';
import TradingTerminal from './components/Trading/TradingTerminal';
import BrokerConfig from './components/BrokerConfig';
import OrdersScreen from './components/OrdersScreen';
import SymbolMaster from './components/SymbolMaster';
import PaperPortfolio from './components/PaperPortfolio';
import LivePortfolio from './components/LivePortfolio';
import WebhookDashboard from './components/WebhookDashboard';
import SettingsPage from './components/SettingsPage';
import ReportsPage from './components/ReportsPage';

const API_URL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const MENU_ITEMS = [
  { id: 'dashboard',  label: 'Dashboard',  path: '/dashboard',  icon: <DashboardIcon fontSize="small" /> },
  { id: 'terminal',   label: 'Terminal',   path: '/terminal',   icon: <ShowChart fontSize="small" /> },
  { id: 'trading',    label: 'Trading',    icon: <AccountBalance fontSize="small" />, children: [
    { id: 'trading-live',  label: 'Live',  path: '/portfolio/live',  icon: <AccountBalance fontSize="small" /> },
    { id: 'trading-paper', label: 'Paper', path: '/portfolio/paper', icon: <AccountBalanceWallet fontSize="small" /> },
  ]},
  { id: 'orders',     label: 'Orders',     path: '/orders',     icon: <ListAlt fontSize="small" /> },
  { id: 'strategies', label: 'Strategies', path: '/strategies', icon: <StrategyIcon fontSize="small" /> },
  { id: 'arbitrage',  label: 'Arbitrage',  path: '/arbitrage',  icon: <TrendingUpOutlined fontSize="small" /> },
  { id: 'reports',    label: 'Reports',    icon: <Assessment fontSize="small" />, children: [
    { id: 'reports-paper', label: 'Paper', path: '/reports/paper', icon: <Assessment fontSize="small" /> },
    { id: 'reports-live',  label: 'Live',  path: '/reports/real',  icon: <Assessment fontSize="small" /> },
  ]},
  { id: 'settings',   label: 'Settings',   icon: <SettingsIcon fontSize="small" />, children: [
    { id: 'settings-symbols',  label: 'Symbols',  path: '/symbols',  icon: <Storage fontSize="small" /> },
    { id: 'settings-brokers',  label: 'Brokers',  path: '/broker',   icon: <SettingsInputComponent fontSize="small" /> },
    { id: 'settings-webhooks', label: 'Webhooks', path: '/webhooks', icon: <Webhook fontSize="small" /> },
  ]},
];

/* ─── MSN Butterfly Theme — compact, light ─── */
const buildTheme = (dark) =>
  createTheme({
    palette: {
      mode: dark ? 'dark' : 'light',
      primary: { main: '#3A5BA0', light: '#6883C4', dark: '#1E3A6E' },
      secondary: { main: '#E8392E', light: '#FF6B5E', dark: '#B01B12' },
      error: { main: '#E8392E' },
      warning: { main: '#FFD700' },
      success: { main: '#4CAF50' },
      info: { main: '#3A5BA0' },
      background: dark
        ? { default: '#1a1a2e', paper: '#222244' }
        : { default: '#F5F6FA', paper: '#ffffff' },
    },
    typography: {
      fontFamily: "'Segoe UI', 'Roboto Condensed', sans-serif",
      fontSize: 13,
      h5: { fontWeight: 700, fontSize: '1.25rem' },
      h6: { fontWeight: 700, fontSize: '1rem' },
      body1: { fontSize: '0.85rem' },
      body2: { fontSize: '0.8rem' },
      caption: { fontSize: '0.72rem' },
    },
    shape: { borderRadius: 8 },
    spacing: 6,
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: dark
              ? 'linear-gradient(90deg, #1a1a2e 0%, #3A5BA0 100%)'
              : 'linear-gradient(90deg, #3A5BA0 0%, #4A7EC8 50%, #6883C4 100%)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
        defaultProps: { elevation: 0, variant: 'outlined' },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600, height: 22, fontSize: '0.7rem' } },
      },
      MuiButton: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600, borderRadius: 6, padding: '4px 14px', fontSize: '0.8rem' } },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: {
            transition: 'transform 0.12s, box-shadow 0.12s',
            '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { padding: '6px 10px', fontSize: '0.8rem' },
          head: { fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { '&:hover': { backgroundColor: 'rgba(58,91,160,0.04)' } },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: '10px 14px', '&:last-child': { paddingBottom: '10px' } } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 8, fontSize: '0.8rem', padding: '4px 12px' } },
      },
    },
  });

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      const t = localStorage.getItem('token');
      const u = localStorage.getItem('user');
      return t && u ? JSON.parse(u) : null;
    } catch { return null; }
  });
  const [apiHealth, setApiHealth] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState({ id: null, el: null });

  const navigate = useNavigate();
  const location = useLocation();

  const theme = buildTheme(darkMode);

  const handleMenuOpen = (id, el) => setMenuAnchor({ id, el });
  const handleMenuClose = () => setMenuAnchor({ id: null, el: null });

  const isActive = (item) => {
    if (item.path) return location.pathname.startsWith(item.path);
    if (item.children) return item.children.some((c) => location.pathname.startsWith(c.path));
    return false;
  };

  const navBtnSx = (active) => ({
    color: 'inherit', fontSize: '0.78rem', fontWeight: 600,
    textTransform: 'none', px: 1.2, minWidth: 0, borderRadius: 0,
    borderBottom: active ? '3px solid #00e5ff' : '3px solid transparent',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderBottom: '3px solid rgba(0,229,255,0.5)' },
  });

  useEffect(() => { checkApiHealth(); checkAuth(); }, []);

  const checkApiHealth = async () => {
    try { const r = await axios.get(`${API_URL}/health`); setApiHealth(r.data); }
    catch { setApiHealth({ status: 'error' }); }
  };
  const checkAuth = () => {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (t && u) setUser(JSON.parse(u));
  };
  const handleAuthSuccess = (data) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user); setAuthOpen(false);
    navigate('/dashboard');
  };
  const handleLogout = () => {
    localStorage.removeItem('token'); localStorage.removeItem('user');
    setUser(null); navigate('/');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* App Bar */}
        <AppBar position="sticky" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ minHeight: 50 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', mr: 2,
              background: 'linear-gradient(135deg, #00bfa5, #00e5ff)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              <TrendingUp sx={{ mr: 0.5, color: '#00e5ff' }} />
              <Typography variant="h6" sx={{
                fontWeight: 800, letterSpacing: '-0.5px',
                background: 'linear-gradient(135deg, #00bfa5, #00e5ff)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                AlgomaticSignals
              </Typography>
            </Box>

            {user && (
              <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'stretch', overflowX: 'auto' }}>
                {MENU_ITEMS.map((item) =>
                  item.children ? (
                    <React.Fragment key={item.id}>
                      <Button
                        startIcon={item.icon}
                        endIcon={<KeyboardArrowDown fontSize="small" />}
                        onClick={(e) => handleMenuOpen(item.id, e.currentTarget)}
                        sx={navBtnSx(isActive(item))}
                        data-testid={`nav-btn-${item.id}`}
                      >
                        {item.label}
                      </Button>
                      <Menu
                        anchorEl={menuAnchor.id === item.id ? menuAnchor.el : null}
                        open={menuAnchor.id === item.id}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      >
                        {item.children.map((child) => (
                          <MenuItem
                            key={child.id}
                            onClick={() => { navigate(child.path); handleMenuClose(); }}
                            selected={location.pathname.startsWith(child.path)}
                            sx={{ fontSize: '0.82rem', gap: 1 }}
                          >
                            {child.icon} {child.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </React.Fragment>
                  ) : (
                    <Button
                      key={item.id}
                      startIcon={item.icon}
                      onClick={() => navigate(item.path)}
                      sx={navBtnSx(isActive(item))}
                      data-testid={`nav-btn-${item.id}`}
                    >
                      {item.label}
                    </Button>
                  )
                )}
              </Box>
            )}

            {!user && <Box sx={{ flexGrow: 1 }} />}

            {apiHealth && (
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%', mr: 0.5,
                  bgcolor: apiHealth.status === 'healthy' ? '#00e676' : '#ff1744',
                  boxShadow: apiHealth.status === 'healthy' ? '0 0 6px #00e676' : '0 0 6px #ff1744',
                }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {apiHealth.status === 'healthy' ? 'Online' : 'Offline'}
                </Typography>
              </Box>
            )}

            <IconButton onClick={() => setDarkMode(!darkMode)} color="inherit" size="small" sx={{ mr: 0.5 }}>
              {darkMode ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
            </IconButton>

            {user ? (
              <>
                <Typography variant="body2" sx={{ mx: 1, color: 'rgba(255,255,255,0.9)', display: { xs: 'none', md: 'block' } }}>
                  {user.name}
                </Typography>
                <Button size="small" onClick={handleLogout} data-testid="logout-btn"
                  sx={{ color: '#ff8a80', '&:hover': { bgcolor: 'rgba(255,138,128,0.1)' } }}>
                  Logout
                </Button>
              </>
            ) : (
              <Button size="small" onClick={() => setAuthOpen(true)} data-testid="login-btn"
                sx={{ color: '#00e5ff', border: '1px solid #00e5ff', '&:hover': { bgcolor: 'rgba(0,229,255,0.1)' } }}>
                Login
              </Button>
            )}
          </Toolbar>
        </AppBar>

        {/* Content */}
        <Container maxWidth="xl" sx={{ mt: 2.5, mb: 3, flexGrow: 1 }}>
          <Routes>
            <Route path="/" element={
              user ? <Navigate to="/dashboard" replace /> : <LandingPage onGetStarted={() => setAuthOpen(true)} dark={darkMode} />
            } />
            <Route path="/dashboard"       element={user ? <Dashboard user={user} apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/terminal"        element={user ? <TradingTerminal apiUrl={API_URL} user={user} /> : <Navigate to="/" replace />} />
            <Route path="/portfolio/paper" element={user ? <PaperPortfolio apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/portfolio/live"  element={user ? <LivePortfolio apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/orders"          element={user ? <OrdersScreen apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/strategies"      element={user ? <StrategyManager apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/arbitrage"       element={user ? <ArbitrageOpportunities apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/webhooks"        element={user ? <WebhookDashboard apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/broker"          element={user ? <BrokerConfig apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/symbols"         element={user ? <SymbolMaster apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="/reports/paper"   element={user ? <ReportsPage apiUrl={API_URL} tradeMode="paper" /> : <Navigate to="/" replace />} />
            <Route path="/reports/real"    element={user ? <ReportsPage apiUrl={API_URL} tradeMode="real" /> : <Navigate to="/" replace />} />
            <Route path="/settings"        element={user ? <SettingsPage apiUrl={API_URL} /> : <Navigate to="/" replace />} />
            <Route path="*"                element={<Navigate to={user ? "/dashboard" : "/"} replace />} />
          </Routes>
        </Container>

        {/* Footer */}
        <Box component="footer" sx={{
          py: 1.5, textAlign: 'center',
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: darkMode ? '#0a0e1a' : '#e8eaf6',
        }}>
          <Typography variant="caption" color="text.secondary">
            2026 AlgomaticSignals &mdash; Algorithmic Trading Platform
          </Typography>
        </Box>
      </Box>

      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)}
        onSuccess={handleAuthSuccess} apiUrl={API_URL} />
    </ThemeProvider>
  );
}

/* ─── Landing Page ─── */
function LandingPage({ onGetStarted, dark }) {
  const features = [
    { icon: <ShowChart sx={{ fontSize: 44 }} />, title: 'Strategy Automation', desc: 'TradingView & Chartink signal integration', color: '#0288d1' },
    { icon: <AccountBalance sx={{ fontSize: 44 }} />, title: 'Arbitrage Detection', desc: 'Spot vs Futures spread scanning every 60s', color: '#00bfa5' },
    { icon: <TrendingUp sx={{ fontSize: 44 }} />, title: 'Paper & Live Trading', desc: 'Test strategies risk-free, then go live', color: '#ff6f00' },
  ];

  return (
    <Box>
      <Paper elevation={0} sx={{
        p: { xs: 4, md: 8 }, mb: 4, textAlign: 'center', borderRadius: 4,
        background: dark
          ? 'linear-gradient(135deg, #1a237e 0%, #0d47a1 40%, #01579b 100%)'
          : 'linear-gradient(135deg, #0d47a1 0%, #1565c0 40%, #0277bd 70%, #00838f 100%)',
        color: '#fff',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.06,
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.5) 20px, rgba(255,255,255,0.5) 22px)',
        }} />
        <Typography variant="h3" fontWeight={800} gutterBottom sx={{ position: 'relative' }}>
          AlgomaticSignals
        </Typography>
        <Typography variant="h6" sx={{ mb: 1, opacity: 0.9, fontWeight: 400, position: 'relative' }}>
          Algorithmic Trading Platform for Indian Markets
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, opacity: 0.8, maxWidth: 600, mx: 'auto', position: 'relative' }}>
          Automate your trading with TradingView signals, Chartink scanners,
          and real-time arbitrage detection across NSE & NFO.
        </Typography>
        <Button variant="contained" size="large" onClick={onGetStarted} data-testid="get-started-btn"
          sx={{
            position: 'relative',
            bgcolor: '#00e5ff', color: '#0d47a1', fontWeight: 700, px: 5, py: 1.5,
            fontSize: '1rem', borderRadius: 3,
            '&:hover': { bgcolor: '#00b8d4', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(0,229,255,0.4)' },
            transition: 'all 0.2s',
          }}>
          Get Started
        </Button>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {features.map((f, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card elevation={2} sx={{
              height: '100%',
              borderTop: `3px solid ${f.color}`,
            }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Box sx={{ color: f.color, mb: 2 }}>{f.icon}</Box>
                <Typography variant="h6" gutterBottom>{f.title}</Typography>
                <Typography variant="body2" color="text.secondary">{f.desc}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={1} sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>Platform Features</Typography>
        <Grid container spacing={1}>
          {[
            'TradingView Webhook Integration', 'Chartink Scanner Integration',
            'Cash & Carry Arbitrage', 'Paper Trading Mode',
            'Real Trading with Shoonya API', 'Risk Management (SL/TP)',
            'Portfolio Tracking', 'Symbol Master with Broker Mapping',
          ].map((f, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00bfa5', flexShrink: 0 }} />
                <Typography variant="body2">{f}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
}

export default App;
